import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { getResultsDetail } from "../lib/api";
import Sidebar from "../components/Sidebar";

// ── Helpers ───────────────────────────────────────────────────────────────────

function tryParseArray(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
}

function confidenceBadge(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  const pct   = n > 1 ? n : Math.round(n * 100);
  const color = pct >= 90 ? "#10b981" : pct >= 70 ? "#f59e0b" : "#ef4444";
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
      background: `${color}18`, color, marginLeft: 6, letterSpacing: "0.03em" }}>
      {pct}%
    </span>
  );
}

// A row in the comparison table
function CompareRow({ field, source, ai, highlight }) {
  const changed = highlight && ai && ai !== "—" && source !== ai;
  return (
    <tr style={{ borderBottom: "1px solid #f1f3f8", borderLeft: changed ? "3px solid #f59e0b" : "3px solid transparent" }}>
      <td style={styles.cmpField}>{field}</td>
      <td style={styles.cmpSource}>{source || <span style={{ color: "#d1d5db" }}>—</span>}</td>
      <td style={styles.cmpAi}>{ai || <span style={{ color: "#d1d5db" }}>—</span>}</td>
    </tr>
  );
}

function SectionHeader({ label }) {
  return (
    <tr>
      <td colSpan={3} style={styles.sectionHeader}>{label}</td>
    </tr>
  );
}

// ── Enrichment attempt helpers ────────────────────────────────────────────────

function stepLabel(step, model, exitReason) {
  if (model === 'jina_pre_check' || (step === 0)) return "Checked website from your upload data";
  if (model === 'jina_verify') return "Verified website ownership";
  if (step === 1) {
    if (exitReason === "addr_standardize_only") return "Standardized address (secondary address row)";
    return "LLM 1 — standardized address and checked company knowledge";
  }
  if (step === 2) return "LLM 2 — searched major company knowledge base";
  if (step === 3) return "LLM 1 — ran web searches";
  if (step === 4) return "LLM 2 — ran web searches and final analysis";
  if (step === 5) {
    if (exitReason?.startsWith("naics_remap") || exitReason?.startsWith("naics_fixed")) return "Industry code corrected";
    if (model === "qc_domain_verify" || exitReason?.startsWith("qc_domain")) return "Verified domain ownership";
    return "Quality control check";
  }
  return `Step ${step}`;
}

function stepOutcome(exitReason, domainFound, model) {
  if (model === 'jina_verify') {
    if (exitReason?.includes('confirmed') && domainFound) return { ok: true, text: `Confirmed: ${domainFound}` };
    if (domainFound) return { ok: false, text: `${domainFound} — could not be confirmed as this company's website` };
    return { ok: false, text: "Website could not be verified" };
  }
  if (model === 'jina_pre_check') {
    if (exitReason === 'pre_jina_confirmed') return { ok: true, text: `Confirmed: ${domainFound}` };
    return { ok: false, text: domainFound ? `${domainFound} — not verified` : "Website in upload data could not be verified" };
  }
  if (!exitReason && !domainFound) return null;
  if (exitReason === "addr_standardize_only") return { ok: null, text: "Address standardized — no enrichment needed for secondary rows" };
  if (exitReason === "step2_trusted" || exitReason === "step2_jina_confirmed") return { ok: true, text: `Found: ${domainFound}` };
  if (exitReason === "step3_confident") return { ok: true, text: `Found: ${domainFound}` };
  if (exitReason === "step3_jina_rejected_mismatch") return { ok: false, text: "Website found but name didn't match this company — discarded" };
  if (exitReason === "final") return { ok: domainFound ? true : false, text: domainFound ? `Found: ${domainFound}` : "No website found after full search" };
  if (exitReason === "qc_domain_confirmed") return { ok: true, text: `Verified: ${domainFound}` };
  if (exitReason === "qc_domain_replaced") return { ok: true, text: `Replaced with: ${domainFound}` };
  if (exitReason === "qc_domain_nulled") return { ok: false, text: "Domain removed — could not be verified" };
  if (exitReason?.startsWith("naics_remap:")) return { ok: null, text: `Corrected: ${exitReason.replace("naics_remap:", "")}` };
  if (exitReason?.startsWith("naics_fixed:")) return { ok: true, text: `Fixed: ${exitReason.replace("naics_fixed:", "")}` };
  if (exitReason?.startsWith("naics_still_null")) return { ok: false, text: "Industry code could not be determined at 6-digit level" };
  if (domainFound) return { ok: true, text: `Found: ${domainFound}` };
  return null;
}

function buildSummary(steps, result, original) {
  const hasDomain    = !!result?.domain_value;
  const hasNaics     = !!result?.naics_code;
  const hasProfile   = !!result?.what_they_do;
  const inputDomain  = original?.website_domain;
  const inputEmail   = original?.email;
  const ranSearch    = steps.some(s => s.step === 3 || s.step === 4);
  const jinaFailed   = steps.some(s => s.model === 'jina_verify' && !s.domain_found?.length);
  const inputEmailDomain = inputEmail ? inputEmail.split('@')[1] : null;

  // Person record — show a person-specific note instead of company-not-found messages
  if (result?.is_person) {
    const step = steps.find(s => s.step === 1 || s.step === 2 || s.step === 3 || s.step === 4);
    const llm  = step?.step <= 2 ? `LLM ${step.step}` : step ? `LLM ${step.step}` : "The AI";
    return `${llm} identified this record as an individual person, not a business. Address standardization was applied. No website, NAICS code, or business profile is generated for person records.`;
  }

  if (hasDomain && hasNaics && hasProfile) return null; // fully enriched — no summary needed

  const parts = [];

  if (inputDomain && !hasDomain) {
    parts.push(`Your upload included website "${inputDomain}" but it could not be confirmed as belonging to this company.`);
  } else if (inputEmailDomain && !hasDomain && steps.some(s => s.step === 1 && s.domain_found)) {
    parts.push(`The email domain "${inputEmailDomain}" was identified but could not be verified as this company's official website.`);
  }

  if (!hasDomain) {
    if (ranSearch) {
      parts.push("Both LLM 1 and LLM 2 ran web searches and could not find a confirmed website for this company. This typically means the company has no public web presence or operates under a different name online.");
    } else if (jinaFailed) {
      parts.push("A candidate website was identified but failed ownership verification.");
    } else {
      parts.push("Neither LLM 1 nor LLM 2 could identify this company from their training knowledge. The company may be too small or local to have been captured in training data.");
    }
  }

  if (!hasNaics && hasDomain) parts.push("Industry classification could not be determined with sufficient confidence.");
  if (!hasProfile && hasDomain) parts.push("A business profile could not be generated.");

  // When NAICS or profile exist but no domain was found, flag them as estimates
  if (!hasDomain && (hasNaics || hasProfile)) {
    parts.push("The industry code and business profile shown are best estimates based on the company name and address from your upload — not from a verified website.");
  }

  return parts.join(" ");
}

function EnrichmentAttempts({ steps, result, original }) {
  if (!Array.isArray(steps) || steps.length === 0) return null;

  // Deduplicate by step number — keep the last record per step (most complete exit_reason)
  const seen = new Map();
  for (const s of steps) {
    const key = `${s.step}:${s.model}`;
    seen.set(key, s); // last one wins
  }
  const dedupedSteps = [...seen.values()];

  const summary = buildSummary(dedupedSteps, result, original);

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <span style={styles.cardTitle}>MasterFile AI Notes</span>
      </div>
      {summary && (
        <div style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 7, padding: "10px 14px", marginBottom: 16 }}>
          <span style={{ color: "#78350f", fontSize: 12, lineHeight: 1.6 }}>{summary}</span>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {dedupedSteps.map((s, i) => {
          const label   = stepLabel(s.step, s.model, s.exit_reason);
          const outcome = stepOutcome(s.exit_reason, s.domain_found, s.model);
          const isSkip  = s.exit_reason === "addr_standardize_only";
          const isGood  = outcome?.ok === true;
          const isBad   = outcome?.ok === false;
          return (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{
                width: 22, height: 22, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                background: isSkip ? "#f1f5f9" : isGood ? "rgba(16,185,129,0.12)" : isBad ? "rgba(239,68,68,0.08)" : "rgba(1,118,206,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700,
                color: isSkip ? "#94a3b8" : isGood ? "#10b981" : isBad ? "#ef4444" : "#0176CE",
              }}>
                {isSkip ? "–" : isGood ? "✓" : isBad ? "✗" : "→"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>{label}</div>
                {outcome && (
                  <div style={{ fontSize: 11, color: isGood ? "#10b981" : isBad ? "#ef4444" : "#9ca3af", marginTop: 2, lineHeight: 1.5 }}>
                    {outcome.text}
                  </div>
                )}
              </div>
              {s.duration_ms > 0 && (
                <span style={{ fontSize: 10, color: "#d1d5db", flexShrink: 0, marginTop: 4 }}>{(s.duration_ms / 1000).toFixed(1)}s</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ResultsDetail() {
  const { jobId, rowNumber } = useParams();
  const navigate             = useNavigate();
  const [searchParams]       = useSearchParams();

  const filter = searchParams.get("filter") || "all";
  const sector = searchParams.get("sector") || "";

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getResultsDetail(jobId, rowNumber, { filter, sector });
      setData(res);
    } catch (e) {
      setError(e.message || "Could not load record.");
    } finally {
      setLoading(false);
    }
  }, [jobId, rowNumber, filter, sector]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  function navToRow(rn) {
    if (!rn) return;
    const sectorPart = sector ? `&sector=${sector}` : "";
    navigate(`/results/${jobId}/records/${rn}?filter=${filter}${sectorPart}`);
  }

  const backHref = `/results/${jobId}/records?filter=${filter}${sector ? `&sector=${sector}` : ""}`;

  const result           = data?.result;
  const original         = data?.original;
  const addresses        = data?.addresses        || [];
  const steps            = data?.steps            || [];
  const duplicateMatches = data?.duplicate_matches || [];
  const prevRow          = data?.prev_row ?? null;
  const nextRow          = data?.next_row ?? null;

  const displayName = result?.company_name_recommended || result?.name1_std || original?.vendor_customer_name || `Row #${rowNumber}`;

  const keyOfferings    = tryParseArray(result?.key_offerings);
  const industriesServed = tryParseArray(result?.industries_served);

  return (
    <div style={styles.page}>
      <div style={styles.topStripe} />
      <div style={styles.body}>
      <Sidebar active="dashboard" />

      {/* ── Main ────────────────────────────────────────────────────────────── */}
      <main style={styles.main}>
        {/* Header */}
        <div style={styles.headerRow}>
          <div style={{ minWidth: 0 }}>
            <Link to={backHref} style={styles.backLink}>← Back to records</Link>
            <h1 style={styles.pageTitle}>{displayName}</h1>
            <p style={styles.pageSub}>Row #{rowNumber} · Job #{jobId}</p>
          </div>
          <div style={styles.headerActions}>
            <button
              style={{ ...styles.navBtn, opacity: prevRow ? 1 : 0.35, cursor: prevRow ? "pointer" : "default" }}
              onClick={() => navToRow(prevRow)}
              disabled={!prevRow}
            >
              ← Row {prevRow || "—"}
            </button>
            <button
              style={{ ...styles.navBtn, opacity: nextRow ? 1 : 0.35, cursor: nextRow ? "pointer" : "default" }}
              onClick={() => navToRow(nextRow)}
              disabled={!nextRow}
            >
              Row {nextRow || "—"} →
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={styles.centerBox}>
            <div style={styles.spinner} />
            <p style={styles.mutedText}>Loading record…</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={styles.errorCard}>
            <p style={{ color: "#f87171", margin: 0 }}>{error}</p>
            <button onClick={fetchDetail} style={styles.retryBtn}>Retry</button>
          </div>
        )}

        {/* Content */}
        {!loading && !error && data && (
          <div style={styles.twoPanel}>

            {/* ── LEFT: Comparison Table ─────────────────────────────────────── */}
            <div style={{ ...styles.card, overflow: "hidden", padding: 0 }}>
              <table style={styles.cmpTable}>
                <thead>
                  <tr style={{ background: "#1f2937" }}>
                    <th style={styles.cmpTh}>Field</th>
                    <th style={styles.cmpTh}>Source Data</th>
                    <th style={styles.cmpTh}>AI Result</th>
                  </tr>
                </thead>
                <tbody>
                  <SectionHeader label="IDENTITY" />
                  <CompareRow
                    field="Vendor #"
                    source={original?.vendor_customer_num}
                    ai={original?.vendor_customer_num}
                    highlight={false}
                  />
                  <CompareRow
                    field="Vendor Name"
                    source={original?.vendor_customer_name}
                    ai={result?.company_name_recommended || result?.name1_std}
                    highlight
                  />
                  <CompareRow
                    field="Vendor Name 2"
                    source={original?.name_line_2}
                    ai={result?.name2_std}
                    highlight
                  />

                  <SectionHeader label="ADDRESS" />
                  {addresses.length > 0
                    ? addresses.map((addr, i) => {
                        const seq = addr.address_seq ?? i + 1;
                        // Use per-address original data from backend (orig_* fields), fall back to top-level original for seq 1
                        const origAddr1 = addr.orig_address_line_1 ?? (i === 0 ? original?.address_line_1 : null);
                        const origAddr2 = addr.orig_address_line_2 ?? (i === 0 ? original?.address_line_2 : null);
                        const origAddr3 = addr.orig_address_line_3 ?? (i === 0 ? original?.address_line_3 : null);
                        const origAddr4 = addr.orig_address_line_4 ?? (i === 0 ? original?.address_line_4 : null);
                        const origCity  = addr.orig_city            ?? (i === 0 ? original?.city            : null);
                        const origState = addr.orig_state_province  ?? (i === 0 ? original?.state_province  : null);
                        const origZip   = addr.orig_zip_postal_code ?? (i === 0 ? original?.zip_postal_code : null);
                        const origCntry = addr.orig_country         ?? (i === 0 ? original?.country         : null);
                        const origPhone = addr.orig_phone           ?? (i === 0 ? original?.phone           : null);
                        const origEmail = addr.orig_email           ?? (i === 0 ? original?.email           : null);
                        return (
                          <React.Fragment key={addr.address_id ?? i}>
                            {addresses.length > 1 && (
                              <tr>
                                <td colSpan={3} style={styles.seqSubHeader}>SEQ {seq}</td>
                              </tr>
                            )}
                            <CompareRow field="Address Line 1" source={origAddr1} ai={addr.address_line_1} highlight />
                            {(addr.address_line_2 || origAddr2) && (
                              <CompareRow field="Address Line 2" source={origAddr2} ai={addr.address_line_2} highlight />
                            )}
                            {(addr.address_line_3 || origAddr3) && (
                              <CompareRow field="Address Line 3" source={origAddr3} ai={addr.address_line_3} highlight />
                            )}
                            {(addr.address_line_4 || origAddr4) && (
                              <CompareRow field="Address Line 4" source={origAddr4} ai={addr.address_line_4} highlight />
                            )}
                            {addr.po_box_std && (
                              <CompareRow field="PO Box" source={null} ai={addr.po_box_std} highlight />
                            )}
                            <CompareRow field="City"        source={origCity}  ai={addr.city}           highlight />
                            <CompareRow field="State"       source={origState} ai={addr.state_province} highlight />
                            <CompareRow field="Postal Code" source={origZip}   ai={addr.zip_postal_code} highlight />
                            <CompareRow field="Country"     source={origCntry} ai={addr.country}        highlight />
                            {(addr.phone_formatted || origPhone) && (
                              <CompareRow field="Phone" source={origPhone} ai={addr.phone_formatted} highlight />
                            )}
                            {(addr.email_std || origEmail) && (
                              <CompareRow field="Email" source={origEmail} ai={addr.email_std} highlight />
                            )}
                          </React.Fragment>
                        );
                      })
                    : <>
                        <CompareRow field="Address Line 1" source={original?.address_line_1} ai={result?.address1_std} highlight />
                        <CompareRow field="Address Line 2" source={original?.address_line_2} ai={result?.address2_std} highlight />
                        <CompareRow field="City"           source={original?.city}           ai={result?.city_std}    highlight />
                        <CompareRow field="State"          source={original?.state_province}  ai={result?.state_std}   highlight />
                        <CompareRow field="Postal Code"    source={original?.zip_postal_code} ai={result?.postal_code_std} highlight />
                        <CompareRow field="Country"        source={original?.country}         ai={result?.country_std} highlight />
                      </>
                  }

                  <SectionHeader label="CONTACT" />
                  <CompareRow field="Phone (E.164)"  source={null}              ai={result?.phone_e164}  highlight={false} />
                  <CompareRow field="Tax ID"         source={original?.tax_id}  ai={result?.taxid_std}   highlight />

                  <SectionHeader label="AI ENRICHED" />
                  <tr style={{ borderBottom: "1px solid #f1f3f8" }}>
                    <td style={styles.cmpField}>Domain</td>
                    <td style={styles.cmpSource}>{original?.website_domain || <span style={{ color: "#d1d5db" }}>—</span>}</td>
                    <td style={styles.cmpAi}>
                      {result?.domain_value
                        ? <>{result.domain_value}{confidenceBadge(result.domain_confidence)}</>
                        : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f3f8" }}>
                    <td style={styles.cmpField}>NAICS</td>
                    <td style={styles.cmpSource}><span style={{ color: "#d1d5db" }}>—</span></td>
                    <td style={styles.cmpAi}>
                      {result?.naics_code
                        ? <span style={{ fontFamily: "monospace", color: "#0176CE", fontSize: 13, fontWeight: 700 }}>{result.naics_code}</span>
                        : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f3f8" }}>
                    <td style={styles.cmpField}>Business Profile</td>
                    <td style={styles.cmpSource}><span style={{ color: "#d1d5db" }}>—</span></td>
                    <td style={styles.cmpAi}>
                      {result?.what_they_do
                        ? <span style={{ fontSize: 11, fontWeight: 600, color: "#10b981", background: "rgba(16,185,129,0.1)", borderRadius: 4, padding: "2px 7px" }}>Yes</span>
                        : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f3f8" }}>
                    <td style={styles.cmpField}>Is Person?</td>
                    <td style={styles.cmpSource}><span style={{ color: "#d1d5db" }}>—</span></td>
                    <td style={styles.cmpAi}>
                      {result?.is_person
                        ? <span style={{ fontSize: 11, fontWeight: 600, color: "#f59e0b", background: "rgba(245,158,11,0.1)", borderRadius: 4, padding: "2px 7px" }}>Yes — Person</span>
                        : <span style={{ fontSize: 11, fontWeight: 600, color: "#10b981", background: "rgba(16,185,129,0.1)", borderRadius: 4, padding: "2px 7px" }}>No — Company</span>}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f3f8" }}>
                    <td style={styles.cmpField}>Is Internal?</td>
                    <td style={styles.cmpSource}><span style={{ color: "#d1d5db" }}>—</span></td>
                    <td style={styles.cmpAi}>
                      {result?.is_internal
                        ? <span style={{ fontSize: 11, fontWeight: 600, color: "#8b5cf6", background: "rgba(139,92,246,0.1)", borderRadius: 4, padding: "2px 7px" }}>Yes — Internal</span>
                        : <span style={{ fontSize: 11, fontWeight: 600, color: "#10b981", background: "rgba(16,185,129,0.1)", borderRadius: 4, padding: "2px 7px" }}>No — External</span>}
                    </td>
                  </tr>
                  <tr>
                    <td style={styles.cmpField}>Status</td>
                    <td style={styles.cmpSource}><span style={{ color: "#d1d5db" }}>—</span></td>
                    <td style={styles.cmpAi}>
                      {result?.ok
                        ? <span style={{ fontSize: 11, fontWeight: 600, color: "#10b981", background: "rgba(16,185,129,0.1)", borderRadius: 4, padding: "2px 7px" }}>OK</span>
                        : <span style={{ fontSize: 11, fontWeight: 600, color: "#ef4444", background: "rgba(239,68,68,0.08)", borderRadius: 4, padding: "2px 7px" }}>Error</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ── RIGHT: Profile Cards ───────────────────────────────────────── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Business Profile */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardTitle}>Business Profile</span>
                  {result?.profile_confidence != null && (
                    <span style={styles.badgeGreen}>{result.profile_confidence}% confidence</span>
                  )}
                </div>
                {result?.what_they_do
                  ? <>
                      <p style={styles.profileText}>{result.what_they_do}</p>
                      {(keyOfferings.length > 0 || industriesServed.length > 0) && (
                        <div style={styles.profileCols}>
                          {industriesServed.length > 0 && (
                            <div>
                              <div style={styles.miniLabel}>Industries Served</div>
                              <ul style={styles.bulletList}>
                                {industriesServed.map((item, i) => (
                                  <li key={i} style={styles.bulletItem}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {keyOfferings.length > 0 && (
                            <div>
                              <div style={styles.miniLabel}>Key Offerings</div>
                              <ul style={styles.bulletList}>
                                {keyOfferings.map((item, i) => (
                                  <li key={i} style={styles.bulletItem}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  : <p style={styles.noDataText}>No business profile available.</p>}
              </div>

              {/* NAICS Classification */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardTitle}>NAICS Classification</span>
                </div>
                {result?.naics_code
                  ? <>
                      <div style={{ marginBottom: 4 }}>
                        <div style={styles.miniLabel}>Primary</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                          <span style={styles.naicsPill}>{result.naics_code}</span>
                          {confidenceBadge(result.naics_confidence)}
                        </div>
                        <div style={{ color: "#374151", fontSize: 13 }}>{result.naics_title || "—"}</div>
                      </div>
                      {(result.naics_alt1_code || result.naics_alt2_code) && (
                        <div style={{ marginTop: 12, borderTop: "1px solid #f1f3f8", paddingTop: 12 }}>
                          <div style={styles.miniLabel}>Alternates</div>
                          {result.naics_alt1_code && (
                            <div style={{ marginBottom: 6 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                                <span style={{ ...styles.naicsPill, fontSize: 12, padding: "2px 8px", background: "rgba(1,118,206,0.08)", color: "#0176CE" }}>{result.naics_alt1_code}</span>
                                {confidenceBadge(result.naics_alt1_confidence)}
                              </div>
                              <div style={{ color: "#6b7280", fontSize: 12 }}>{result.naics_alt1_title}</div>
                            </div>
                          )}
                          {result.naics_alt2_code && (
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                                <span style={{ ...styles.naicsPill, fontSize: 12, padding: "2px 8px", background: "rgba(1,118,206,0.08)", color: "#0176CE" }}>{result.naics_alt2_code}</span>
                                {confidenceBadge(result.naics_alt2_confidence)}
                              </div>
                              <div style={{ color: "#6b7280", fontSize: 12 }}>{result.naics_alt2_title}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  : <p style={styles.noDataText}>Not classified.</p>}
              </div>

              {/* Domain */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardTitle}>Domain</span>
                  {result?.domain_value && (
                    <span style={styles.badgeGreen}>Verified</span>
                  )}
                </div>
                {result?.domain_value
                  ? <>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <a
                          href={`https://${result.domain_value}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#0176CE", fontWeight: 600, fontSize: 14, textDecoration: "none" }}
                        >
                          {result.domain_value}
                        </a>
                        {confidenceBadge(result.domain_confidence)}
                      </div>
                      {result.domain_source && (() => {
                        const srcMap = { INPUT_DOMAIN_RAW: "Input Domain (Verified)", SONNET_KNOWLEDGE: "LLM2", WEB_SEARCH: "LLM Search", NOT_FOUND: null, ADDR_ONLY: null };
                        const label = srcMap[result.domain_source] ?? (/haiku/i.test(result.domain_source) ? "LLM1" : "LLM");
                        return label ? <div style={{ color: "#9ca3af", fontSize: 12 }}>Source: {label}</div> : null;
                      })()}
                    </>
                  : <>
                      <p style={styles.noDataText}>No website found.</p>
                      {original?.website_domain && (
                        <p style={{ ...styles.noDataText, fontSize: 11, marginTop: 4 }}>
                          Input contained: <span style={{ fontFamily: "monospace", color: "#ef4444" }}>{original.website_domain}</span> — could not be verified
                        </p>
                      )}
                    </>}
              </div>

              {/* Vendor Attributes */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardTitle}>Vendor Attributes</span>
                </div>
                <div style={styles.attrList}>
                  <div style={styles.attrRow}>
                    <span style={styles.attrLabel}>Is a Person?</span>
                    {result?.is_person
                      ? <span style={styles.badgeAmber}>Yes — Person</span>
                      : <span style={styles.badgeGreen}>No — Company</span>}
                  </div>
                  <div style={styles.attrRow}>
                    <span style={styles.attrLabel}>Is Internal?</span>
                    {result?.is_internal
                      ? <span style={styles.badgePurple}>Yes — Internal</span>
                      : <span style={styles.badgeGreen}>No — External</span>}
                  </div>
                  <div style={styles.attrRow}>
                    <span style={styles.attrLabel}>Enrichment Status</span>
                    {result?.ok
                      ? <span style={styles.badgeGreen}>OK</span>
                      : <span style={styles.badgeRed}>Error</span>}
                  </div>
                  {!result?.ok && result?.error_message && (
                    <div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(239,68,68,0.05)", borderRadius: 6, borderLeft: "3px solid #ef4444" }}>
                      <span style={{ fontSize: 12, color: "#ef4444" }}>{result.error_message}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Potential Matches — only shown when duplicates exist */}
              {duplicateMatches.length > 0 && (
                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <span style={styles.cardTitle}>Potential Matches</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#ef4444", background: "rgba(239,68,68,0.08)", borderRadius: 4, padding: "2px 8px" }}>
                      {duplicateMatches.length} match{duplicateMatches.length > 1 ? "es" : ""}
                    </span>
                  </div>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 12px" }}>
                    Other vendors in your master file share the same domain or address as this record.
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {duplicateMatches.map((m, i) => {
                      const matchTypes = [];
                      if (m.match_domain_same)  matchTypes.push({ label: "Same domain",   color: "#0176CE" });
                      if (m.match_domain_cross) matchTypes.push({ label: "Cross-system domain", color: "#ef4444" });
                      if (m.match_address)      matchTypes.push({ label: "Same address",   color: "#f59e0b" });
                      const name = m.company_name_recommended || m.record_name || m.record_number;
                      const loc  = [m.city, m.state_province].filter(Boolean).join(", ");
                      return (
                        <div key={i} style={{ border: "1px solid #f1f5f9", borderRadius: 7, padding: "10px 12px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 2 }}>{name}</div>
                              <div style={{ fontSize: 11, color: "#9ca3af" }}>
                                {m.system_name} · {m.record_number}{loc ? ` · ${loc}` : ""}
                              </div>
                              {m.domain_value && (
                                <div style={{ fontSize: 11, color: "#0176CE", marginTop: 3 }}>{m.domain_value}</div>
                              )}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                              {matchTypes.map((t, j) => (
                                <span key={j} style={{ fontSize: 10, fontWeight: 700, color: t.color, background: `${t.color}12`, borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap" }}>
                                  {t.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* MasterFile AI Notes */}
              {steps.length > 0 && (
                <EnrichmentAttempts steps={steps} result={result} original={original} />
              )}

            </div>
          </div>
        )}
      </main>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  page:          { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', system-ui, sans-serif" },
  topStripe:     { height: 4, background: "linear-gradient(90deg, #0176CE, #F37428)", flexShrink: 0 },
  body:          { display: "flex", flex: 1 },
  main:          { flex: 1, padding: "40px 48px", overflowY: "auto" },
  headerRow:     { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 20 },
  backLink:      { color: "#6b7280", fontSize: 13, textDecoration: "none", display: "block", marginBottom: 8 },
  pageTitle:     { color: "#111827", fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  pageSub:       { color: "#6b7280", fontSize: 14, margin: "4px 0 0" },
  headerActions: { display: "flex", gap: 10, alignItems: "center", flexShrink: 0 },
  navBtn:        { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#374151", cursor: "pointer", fontSize: 13, fontWeight: 500, padding: "9px 16px", whiteSpace: "nowrap" },
  centerBox:     { textAlign: "center", padding: "80px 24px", display: "flex", flexDirection: "column", alignItems: "center" },
  mutedText:     { color: "#6b7280", marginTop: 16, fontSize: 14 },
  spinner:       { width: 32, height: 32, border: "3px solid #e2e8f0", borderTop: "3px solid #0176CE", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  errorCard:     { background: "#fff", border: "1px solid #fecaca", borderRadius: 12, padding: "32px 24px", textAlign: "center" },
  retryBtn:      { marginTop: 12, background: "transparent", border: "1px solid #0176CE", borderRadius: 6, color: "#0176CE", cursor: "pointer", fontSize: 13, padding: "7px 16px" },
  twoPanel:      { display: "grid", gridTemplateColumns: "55fr 45fr", gap: 20, alignItems: "start" },
  card:          { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 },
  cardHeader:    { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  cardTitle:     { color: "#111827", fontSize: 14, fontWeight: 700 },
  cmpTable:      { width: "100%", borderCollapse: "collapse" },
  cmpTh:         { color: "#fff", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", padding: "12px 16px", textAlign: "left" },
  cmpField:      { color: "#6b7280", fontSize: 12, fontWeight: 600, padding: "10px 16px", width: 130, verticalAlign: "middle", borderRight: "1px solid #f1f5f9" },
  cmpSource:     { color: "#374151", fontSize: 12, padding: "10px 16px", verticalAlign: "middle", borderRight: "1px solid #f1f5f9" },
  cmpAi:         { color: "#111827", fontSize: 12, fontWeight: 500, padding: "10px 16px", verticalAlign: "middle" },
  sectionHeader: { background: "#0176CE", color: "#fff", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", padding: "8px 16px" },
  seqSubHeader:  { background: "rgba(1,118,206,0.06)", color: "#0176CE", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", padding: "6px 16px" },
  profileText:   { color: "#374151", fontSize: 13, lineHeight: 1.7, margin: "0 0 12px" },
  profileCols:   { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 8 },
  miniLabel:     { color: "#9ca3af", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 },
  bulletList:    { margin: 0, padding: "0 0 0 16px" },
  bulletItem:    { color: "#374151", fontSize: 12, lineHeight: 1.8 },
  noDataText:    { color: "#9ca3af", fontSize: 13, margin: 0, fontStyle: "italic" },
  naicsPill:     { display: "inline-block", background: "rgba(1,118,206,0.08)", color: "#0176CE", fontFamily: "monospace", fontWeight: 700, fontSize: 15, padding: "4px 12px", borderRadius: 6 },
  attrList:      { display: "flex", flexDirection: "column", gap: 10 },
  attrRow:       { display: "flex", alignItems: "center", justifyContent: "space-between" },
  attrLabel:     { color: "#6b7280", fontSize: 13 },
  badgeGreen:    { fontSize: 11, fontWeight: 600, color: "#10b981", background: "rgba(16,185,129,0.1)", borderRadius: 5, padding: "3px 9px" },
  badgeAmber:    { fontSize: 11, fontWeight: 600, color: "#f59e0b", background: "rgba(245,158,11,0.1)", borderRadius: 5, padding: "3px 9px" },
  badgePurple:   { fontSize: 11, fontWeight: 600, color: "#8b5cf6", background: "rgba(139,92,246,0.1)", borderRadius: 5, padding: "3px 9px" },
  badgeRed:      { fontSize: 11, fontWeight: 600, color: "#ef4444", background: "rgba(239,68,68,0.08)", borderRadius: 5, padding: "3px 9px" },
};
